import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  ACTIONS_TEXT,
  IMAGE_SRC,
  ITEMS_PAGE_LABEL,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { TableHeaderComponent } from 'src/app/components/shared/table/components/table-header/table-header.component';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  CreateDeliveryRequest,
  CreateDeliveryResponse,
} from 'src/app/core/models/operations/operations-delivery.model';
import {
  CreatePickupRequest,
  CreatePickupResponse,
} from 'src/app/core/models/operations/operations-pickup-stop.model';
import {
  OperationsRouteDetail,
  TableOperationsRouteDetails,
} from 'src/app/core/models/operations/operations-route-details.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OperationsUtils } from 'src/app/core/utils/operations/operations.utils';
import { currentLocale } from 'src/app/locale.config';
import { EditOperationsStopComponent } from '../edit-operations-stop/edit-operations-stop.component';
import { DENSITY_LIST } from '../edit-operations-stop/edit-operations-stop.constants';
import {
  ROUTE_DETAILS_CONSTANTS,
  RouteDataType,
} from '../route-details.constants';
import {
  BUTTON_TEXTS,
  ColumnIds,
  ColumnText,
  DATE_PLACEHOLDER,
  EXCEL_FILE_NAME,
} from './pending-stops.constants';

@Component({
  selector: 'app-pending-stops',
  templateUrl: './pending-stops.component.html',
  styleUrls: ['../details/details.component.scss'],
  standalone: false,
})
export class PendingStopsComponent implements OnInit {
  @ViewChild('header') header!: TableHeaderComponent;

  destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);

  readonly ITEMS_PAGE_LABEL = ITEMS_PAGE_LABEL;
  readonly CONSTANTS = ROUTE_DETAILS_CONSTANTS;
  readonly SRC = IMAGE_SRC;
  readonly DENSITY_LIST = DENSITY_LIST;
  readonly ACTIONS_TEXT = ACTIONS_TEXT;
  readonly DATE_PLACEHOLDER = DATE_PLACEHOLDER;
  readonly BUTTONS = BUTTON_TEXTS;
  readonly IMAGES = IMAGE_SRC;
  readonly SpecialCharacters = SpecialCharacters;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly HEADERS = ColumnText;

  selectedRoute = '';
  dataSource = new MatTableDataSource<TableOperationsRouteDetails>();
  expandedTable: TableOperationsRouteDetails[] = [];
  rowCount: number[] = [];
  tableRowElement!: HTMLCollection;
  routerData!: RouteDataType;
  toFromDate!: string;
  planDate: Date = new Date();

  user!: UserAdmin;
  locationCode = '';

  constructor(
    public readonly commonUtils: CommonUtils,
    private readonly dialog: MatDialog,
    private readonly summaryService: SummaryService,
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly operationsUtils: OperationsUtils,
    private readonly dataService: DataService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.routerData = this.router.currentNavigation()?.extras
      .state as unknown as RouteDataType;
    this.dataService.addedStop$.subscribe(() => {
      this.updateRows();
      this.cdr.detectChanges();
    });
  }

  /**
   * Initializes route context (date and route id), subscribes to route param changes,
   * and kicks off the first data load via `updateRows()`.
   *
   * - `planDate` is set from the router-provided date.
   * - `toFromDate` is the formatted string used in backend calls.
   * - `selectedRoute` is populated from `ActivatedRoute.params`.
   * - Calls `updateRows()` to load user context and route details.
   */
  ngOnInit() {
    const transformedDate = this.datePipe.transform(
      this.routerData?.selectedDate,
      'dd-MM-yyyy',
    );

    this.planDate = new Date(this.routerData?.selectedDate);
    this.toFromDate = transformedDate ?? '';

    // Keep route id in sync with URL changes
    this.route?.params?.subscribe((param) => {
      this.selectedRoute = param.id;
    });

    this.updateRows();
  }

  /**
   * Loads the current user context and refreshes route details.
   * If the user's location changes while on this screen, navigates back to the route summary.
   *
   * @remarks
   * - Subscribes until component destroy using `takeUntilDestroyed(this.destroy)`.
   * - Calls `getRouteDetails()` whenever user context emits.
   * - Detects location change to prevent stale view and navigates to summary route.
   */
  updateRows() {
    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        this.user = user;
        this.fetchTableData();
        // navigate back to route summary if location is changed in route details screen
        const previousLocationCode = this.locationCode;
        this.locationCode = user.location;

        if (
          previousLocationCode &&
          previousLocationCode !== this.locationCode
        ) {
          this.router.navigate([
            '/',
            currentLocale(),
            'operations',
            'route-summary',
          ]);
        }
      });
  }

  /**
   * Applies a free-text search to the table using Angular Material's filter string,
   * and then recomputes `displayStop` so only the first row of each consecutive `index` group
   * is marked for display (grouping behavior).
   *
   * @param searchValue - Raw input from the header search; will be normalized to lowercase/trimmed.
   *
   * @remarks
   * - `MatTableDataSource` default filter is case-insensitive on lowercase JSON stringified rows.
   * - Grouping logic assumes rows are sorted by `index` and that same-index rows are adjacent.
   * - If a paginator is attached, consider resetting to the first page here.
   */
  search(searchValue: string): void {
    const trimmedValue = (searchValue ?? '').trim().toLowerCase();
    this.dataSource.filter = trimmedValue;

    // Recompute grouping flags after filter has been applied
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex !== data.index) {
        data.displayStop = true; // first of group
        currentIndex = data.index;
      } else {
        data.displayStop = false; // subsequent in same group
      }
    });
  }

  /**
   * Clears the table filter (shows all rows) and recomputes `displayStop`
   * so only the first item of each consecutive `index` group remains marked for display.
   *
   * @remarks
   * - Uses empty string `''` to clear the `MatTableDataSource` filter.
   * - Grouping logic is order-dependent (expects rows with same `index` to be adjacent).
   */
  clearSearch() {
    this.dataSource.filter = '';

    // Recompute grouping flags for the unfiltered data
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex === data.index) {
        data.displayStop = false;
      } else {
        data.displayStop = true;
        currentIndex = data.index;
      }
    });
  }

  /**
   * Exports the currently **filtered** table rows to Excel.
   * Builds a flat array of row objects mapped to exportable columns,
   * and delegates the file creation to `commonUtils.exportExcel`.
   *
   * @remarks
   * Uses `dataSource.filteredData`, ensuring the export reflects the active filter/search.
   * Column headers come from `ColumnText`, and file name from `EXCEL_FILE_NAME`.
   */
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((value) => {
      return {
        stopType: value.type,
        density: value.density,
        cosignee: value.cosignee,
        address: `${value.address} ${value.city} ${value.postal}`,
        stopDate: !isNaN(value?.stopDate?.getTime()) ? value?.stopDate : null,
        completionTime: value.completionTime,
        trackingNumber: value.trackingNumber,
        scanCode: value.scanCode,
        description: value.description,
        amtDue: value.amtDue,
        amtCollected: value.amtCollected,
      };
    });
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      filteredData,
    );
  }

  getRowSpan(data: TableOperationsRouteDetails) {
    let returnValue = 0;
    if (data.displayStop) {
      returnValue = this.dataSource.filteredData.filter(
        (filteredData) => filteredData.index === data.index,
      ).length;
    }
    return returnValue;
  }

  /**
   * Opens the Edit Operations Stop dialog for a given route detail.
   * Prior to opening, it clears both the datasource filter and the visible search input
   * to avoid any UI misalignment (e.g., colspan/rowspan based on filtered groups).
   *
   * Flow:
   * 1) Clears search state (datasource + header input).
   * 2) Builds `country$` observable for the location profile.
   * 3) Resolves full `routeDetails` object from current datasource rows.
   * 4) Opens `EditOperationsStopComponent` dialog with the necessary data.
   * 5) After dialog closes, if the operation succeeded (HTTP 200):
   *    - Refreshes route details.
   *    - Clears search again (keeps UI/grouping consistent after refresh).
   *    - Notifies via `dataService.notifyStopAdded()`.
   * 6) If required context is missing, shows a "cannot find route" banner.
   *
   * @param routeDetail - The row/route detail the user intends to edit.
   */
  editStop(routeDetail: OperationsRouteDetail, index: number) {
    // Clearing the search field to avoid misalignment issues (both datasource + header input)
    this.clearSearch();
    this.header?.clearSearchText();

    const country$ = this.locationService.getLocationProfile(this.locationCode);

    const routeDetails = this.operationsUtils.getSelectedRoute(
      routeDetail,
      this.dataSource.data,
    );

    if (!!routeDetails && !!this.selectedRoute && !!this.planDate) {
      this.dialog
        .open(EditOperationsStopComponent, {
          disableClose: true,
          minWidth: '70.25rem',
          minHeight: '30.75rem',
          data: {
            routeDetails: routeDetails,
            country: country$,
            route: this.selectedRoute,
            planDate: this.planDate,
            viewIndex: index + 1,
          },
        })
        .afterClosed()
        .subscribe(
          (value: {
            request: CreateDeliveryRequest | CreatePickupRequest;
            response: CreateDeliveryResponse | CreatePickupResponse;
          }) => {
            this.commonUtils.updateLoadingModal(false);
            if (value?.response?.code === 200) {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
            this.dataService.notifyPendingStopAdded();
            this.fetchTableData();
          },
        );
    } else {
      this.commonUtils.updateBannerType(BANNER_TYPE.CANNOT_FIND_ROUTE);
    }
  }

  /**
   * Fetches and populates the details table data for the selected route and date.
   *
   * This method calls `summaryService.getRouteDetails(...)` to retrieve route scan
   * details for the current `locationCode`, the selected `toFromDate` range (start and end
   * set to the same value), and the `selectedRoute`.
   *
   * Processing steps:
   *  1. Pushes the total number of scans into `rowCount`.
   *  2. Filters the scan list to only include stops with state === 'Pending'.
   *  3. Uses `operationsUtils.createDetailsTable(...)` to transform the pending stops
   *     into a table-friendly model.
   *  4. Sorts the transformed results by ascending `index`.
   *  5. Assigns the sorted data to `dataSource.data` for rendering in the table.
   *
   **/
  private fetchTableData() {
    this.summaryService
      .getRouteDetails(
        this.locationCode,
        this.toFromDate,
        this.toFromDate,
        this.selectedRoute,
      )
      .pipe(take(1))
      .subscribe((res) => {
        this.rowCount.push(res.response?.scan?.length);
        const pendingStops = res.response.scan.filter((stops) => {
          return stops.state === 'Pending';
        });
        const returnVals =
          this.operationsUtils.createDetailsTable(pendingStops);

        returnVals.sort((a, b) => a.index - b.index);

        this.dataSource.data = returnVals;
        this.commonUtils.updateLoadingModal(false);
      });
  }
}
